import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import {
    BABOOR_ENVIRONMENT,
    BaboorEnvironmentConfig,
} from '@baboor/baboor-core'
import type {
    Expense,
    InsertExpense,
    UpdateExpense,
    ExpenseCategory,
    InsertExpenseCategory,
    UpdateExpenseCategory,
    DashboardData,
    ProfitLoss,
    ExpenseQueryParams,
} from '@baboor/baboor-models'
import { ApiResponse } from '@baboor/common-models'
import { toHttpParams } from '@baboor/common-util'
import { Observable } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class FinancialApiService {
    private apiUrl: string

    constructor(
        private http: HttpClient,
        @Inject(BABOOR_ENVIRONMENT) private env: BaboorEnvironmentConfig,
    ) {
        this.apiUrl = `${env.apiUrl}/v1/financial`
    }

    getDashboard(): Observable<ApiResponse<DashboardData>> {
        return this.http.get<ApiResponse<DashboardData>>(
            `${this.apiUrl}/dashboard`,
        )
    }

    getRevenue(
        dateFrom?: string,
        dateTo?: string,
    ): Observable<ApiResponse<{ date: string; amount: string }[]>> {
        return this.http.get<ApiResponse<{ date: string; amount: string }[]>>(
            `${this.apiUrl}/revenue`,
            {
                params: toHttpParams({ dateFrom, dateTo }),
            },
        )
    }

    getExpenses(
        params?: ExpenseQueryParams,
    ): Observable<ApiResponse<Expense[]>> {
        return this.http.get<ApiResponse<Expense[]>>(
            `${this.apiUrl}/expenses`,
            { params: toHttpParams(params ?? {}) },
        )
    }

    getExpense(id: string): Observable<ApiResponse<Expense>> {
        return this.http.get<ApiResponse<Expense>>(
            `${this.apiUrl}/expenses/${id}`,
        )
    }

    createExpense(data: InsertExpense): Observable<ApiResponse<Expense>> {
        return this.http.post<ApiResponse<Expense>>(
            `${this.apiUrl}/expenses`,
            data,
        )
    }

    updateExpense(
        id: string,
        data: UpdateExpense,
    ): Observable<ApiResponse<Expense>> {
        return this.http.patch<ApiResponse<Expense>>(
            `${this.apiUrl}/expenses/${id}`,
            data,
        )
    }

    deleteExpense(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/expenses/${id}`,
        )
    }

    getExpenseCategories(): Observable<ApiResponse<ExpenseCategory[]>> {
        return this.http.get<ApiResponse<ExpenseCategory[]>>(
            `${this.apiUrl}/expense-categories`,
        )
    }

    createExpenseCategory(
        data: InsertExpenseCategory,
    ): Observable<ApiResponse<ExpenseCategory>> {
        return this.http.post<ApiResponse<ExpenseCategory>>(
            `${this.apiUrl}/expense-categories`,
            data,
        )
    }

    updateExpenseCategory(
        id: string,
        data: UpdateExpenseCategory,
    ): Observable<ApiResponse<ExpenseCategory>> {
        return this.http.patch<ApiResponse<ExpenseCategory>>(
            `${this.apiUrl}/expense-categories/${id}`,
            data,
        )
    }

    deleteExpenseCategory(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/expense-categories/${id}`,
        )
    }

    getProfitLoss(
        dateFrom?: string,
        dateTo?: string,
    ): Observable<ApiResponse<ProfitLoss>> {
        return this.http.get<ApiResponse<ProfitLoss>>(
            `${this.apiUrl}/profit-loss`,
            {
                params: toHttpParams({ dateFrom, dateTo }),
            },
        )
    }
}
